import axiosClient from "@/services/shared/api/axios-client";

/**
 * Single exercise in module test pool (GET response item).
 * Spec: add-module-test-pool-crud-backend
 */
export interface ModuleTestPoolExerciseItem {
  id: string;
  type: string;
  data?: unknown;
  order: number;
  title?: string;
}

/**
 * Response of GET module test pool (full exercises).
 * Spec: add-module-test-pool-crud-backend, add-module-test-pool-dedicated-admin-frontend
 */
export interface ModuleTestPoolResponse {
  exercises: ModuleTestPoolExerciseItem[];
  poolSizeBelowRecommended?: boolean;
}

/**
 * GET admin/modules/{moduleId}/test-pool — list exercises in the pool with full data.
 * Supports legacy response { exerciseIds } by returning empty exercises until backend is updated.
 */
export const apiGetModuleTestPool = async (
  moduleId: string
): Promise<ModuleTestPoolResponse> => {
  const response = await axiosClient.get<ModuleTestPoolResponse & { exerciseIds?: string[] }>(
    `/admin/modules/${moduleId}/test-pool`
  );
  const data = response.data;
  if (data.exercises != null) {
    return { exercises: data.exercises, poolSizeBelowRecommended: data.poolSizeBelowRecommended };
  }
  if (Array.isArray((data as { exerciseIds?: string[] }).exerciseIds)) {
    return { exercises: [], poolSizeBelowRecommended: data.poolSizeBelowRecommended };
  }
  return { exercises: [], poolSizeBelowRecommended: data.poolSizeBelowRecommended };
};

/** Payload for creating or updating a pool exercise (AnswerData = ids of correct options for multiple-choice). */
export interface ModuleTestPoolExercisePayload {
  type: string;
  data: unknown;
  title?: string;
  answerData?: number[];
}

/**
 * POST admin/modules/{moduleId}/test-pool/exercises — create exercise in pool.
 */
export const apiPostModuleTestPoolExercise = async (
  moduleId: string,
  payload: ModuleTestPoolExercisePayload
): Promise<ModuleTestPoolExerciseItem> => {
  const response = await axiosClient.post<ModuleTestPoolExerciseItem>(
    `/admin/modules/${moduleId}/test-pool/exercises`,
    payload
  );
  return response.data;
};

/**
 * PUT admin/modules/{moduleId}/test-pool/exercises/{exerciseId} — update exercise in pool.
 */
export const apiPutModuleTestPoolExercise = async (
  moduleId: string,
  exerciseId: string,
  payload: ModuleTestPoolExercisePayload
): Promise<ModuleTestPoolExerciseItem> => {
  const response = await axiosClient.put<ModuleTestPoolExerciseItem>(
    `/admin/modules/${moduleId}/test-pool/exercises/${exerciseId}`,
    payload
  );
  return response.data;
};

/**
 * DELETE admin/modules/{moduleId}/test-pool/exercises/{exerciseId} — remove exercise from pool.
 */
export const apiDeleteModuleTestPoolExercise = async (
  moduleId: string,
  exerciseId: string
): Promise<void> => {
  await axiosClient.delete(
    `/admin/modules/${moduleId}/test-pool/exercises/${exerciseId}`
  );
};

/**
 * PUT admin/modules/{moduleId}/test-pool/exercises/order — update order.
 */
export const apiPutModuleTestPoolExercisesOrder = async (
  moduleId: string,
  exerciseIds: string[]
): Promise<void> => {
  await axiosClient.put(
    `/admin/modules/${moduleId}/test-pool/exercises/order`,
    { exerciseIds }
  );
};
